import ListCard from '@Components/ListCard/ListCard';
import NewAssetStatus from '@Components/Modals/AssetStatus/AssetStatusModal';
import DeleteAssetStatus from '@Components/Modals/AssetStatus/DeleteAssetStatus';
import AssetStatusServices from '@Services/AssetStatus.services.ts';
import { AssetStatus } from '@Types/Asset.types';
import { PaginatedResponse } from '@Types/Response.types';
import { PAGE_PROP, PAGE_SIZE, PAGINATED_DEFAULT, PATHS, SEARCH_PROP, TABLE_STRUCTURES } from '@Utils/constants';
import { handleRequestError } from '@Utils/request.utils';
import { Button, Icons, Pagination, SearchBox, SectionCard, useTopHat } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const AssetStatuses = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [assetStatus, setAssetStatus] = useState<PaginatedResponse<AssetStatus>>(PAGINATED_DEFAULT);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const currentPage = Number(searchParams.get(PAGE_PROP) ?? '1');
  const searchQuery = searchParams.get(SEARCH_PROP) ?? '';
  const [showAssetModal, setShowAssetModal] = useState<boolean>(false);
  const [showDeleteAssetModal, setShowDeleteAssetModal] = useState<boolean>(false);
  const [selectedAssetStatus, setSelectedAssetStatus] = useState<AssetStatus | undefined>(undefined);

  useTopHat({
    activeNav: 'nav-settings',
    activeTab: undefined,
    tabs: [],
    breadcrumbs: [
      { title: 'Settings', href: PATHS.SETTINGS, id: 'Settings' },
      {
        title: 'Asset Status',
        href: `${PATHS.ASSET_STATUS}?page=${1}`,
        id: 'Asset Status',
      },
    ],
  });

  const handleNewPage = (newPage: number, data: PaginatedResponse<AssetStatus>) => {
    if (newPage <= 0 || newPage > assetStatus.totalPages) {
      return;
    }
    const page = newPage > data.totalPages ? data.prevPage : newPage;
    if (page !== currentPage) {
      searchParams.set(PAGE_PROP, page?.toString() || '1');
      setSearchParams(searchParams);
    }
  };

  const getAssetStatus = () => {
    setSelectedAssetStatus(undefined);
    AssetStatusServices.searchAssetStatus({ search: searchQuery }, currentPage)
      .then(({ data }) => {
        setAssetStatus(data);
        setErrorMessage(undefined);
        handleNewPage(currentPage, data);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setIsLoading(true);
    getAssetStatus();
  }, [searchQuery, currentPage]);

  const handleSearchQueryChange = (newValue: string) => {
    searchParams.set(SEARCH_PROP, newValue);
    searchParams.set(PAGE_PROP, '1');
    if (!newValue) {
      searchParams.delete(SEARCH_PROP);
    }
    setSearchParams(searchParams);
  };

  const handleAssetStatusAction = (id: string, setModal: (show: boolean) => void) => {
    setSelectedAssetStatus(assetStatus.docs.find((asset: AssetStatus) => asset.id === id));
    setModal(true);
  };

  return (
    <div className='grid'>
      <div className='col-12 col-md-3'>
        <SectionCard>
          <SearchBox
            id='search-asset-status'
            fullWidth
            focusOnRender
            value={searchQuery}
            defaultValue={searchQuery}
            onChange={handleSearchQueryChange}
          />
          <Button type='primary' className='button-with-icon' fullWidth onClick={() => setShowAssetModal(true)}>
            {Icons.Plus} Add new Asset Status
          </Button>
        </SectionCard>
      </div>
      <div className='col-12 col-md-9'>
        <ListCard<AssetStatus>
          title="Asset Status"
          structure={TABLE_STRUCTURES.ASSET_STATUS}
          data={assetStatus.docs}
          loading={isLoading}
          withActions={{
            edit: (editId) => handleAssetStatusAction(editId, setShowAssetModal),
            delete: (deleteId) => handleAssetStatusAction(deleteId, setShowDeleteAssetModal),
          }}
          error={errorMessage}
          searchEntered={!!searchQuery}
        />
      </div>
      <div className='col-12 content-middle content-right-md'>
        <Pagination
          count={assetStatus.totalDocs}
          perPage={PAGE_SIZE}
          currentPage={currentPage - 1}
          onChange={(page) => handleNewPage(page + 1, assetStatus)}
        />
      </div>
      <NewAssetStatus
        show={showAssetModal}
        assetStatus={selectedAssetStatus}
        onClose={() => {
          setShowAssetModal(false);
          setSelectedAssetStatus(undefined);
        }}
        onSuccess={() => getAssetStatus()}
      />
      <DeleteAssetStatus
        show={showDeleteAssetModal}
        assetStatus={selectedAssetStatus}
        onClose={() => {
          setShowDeleteAssetModal(false);
          setSelectedAssetStatus(undefined);
        }}
        onSuccess={() => getAssetStatus()}
      />
    </div>
  );
};

export default AssetStatuses;
